import { Button } from 'antd';
import { IconCheck, IconLoader2 } from '@tabler/icons-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { AppPrimaryButtonProps } from './AppPrimaryButton.types';
import styles from './AppPrimaryButton.module.css';

export const AppPrimaryButton: React.FC<AppPrimaryButtonProps> = ({
  title,
  compact = false,
  iconOnly = false,
  stateful = true,
  showSuccessWhenDone = false,
  successDurationMs = 2000,
  loading,
  disabled,
  onClick,
  children,
  className,
  style,
  ...props
}) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const successTimeoutRef = useRef<number | null>(null);
  const previousLoadingRef = useRef(false);

  const isLoadingControlled = typeof loading === 'boolean';
  const resolvedLoading = isLoadingControlled ? Boolean(loading) : internalLoading;

  const clearSuccessTimeout = useCallback(() => {
    if (successTimeoutRef.current) {
      window.clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
  }, []);

  const triggerSuccessState = useCallback(() => {
    setShowSuccess(true);
    clearSuccessTimeout();
    successTimeoutRef.current = window.setTimeout(() => {
      setShowSuccess(false);
      successTimeoutRef.current = null;
    }, successDurationMs);
  }, [clearSuccessTimeout, successDurationMs]);

  useEffect(() => {
    if (!stateful || !isLoadingControlled || !showSuccessWhenDone) {
      previousLoadingRef.current = resolvedLoading;
      return;
    }

    if (previousLoadingRef.current && !resolvedLoading) {
      triggerSuccessState();
    }
    previousLoadingRef.current = resolvedLoading;
  }, [isLoadingControlled, resolvedLoading, showSuccessWhenDone, stateful, triggerSuccessState]);

  useEffect(
    () => () => {
      clearSuccessTimeout();
    },
    [clearSuccessTimeout]
  );

  const handleClick: React.MouseEventHandler<HTMLElement> = async event => {
    if (!onClick) {
      return;
    }

    if (!stateful || isLoadingControlled) {
      onClick(event);
      return;
    }

    const maybePromise = onClick(event) as unknown;
    if (
      !maybePromise ||
      typeof (maybePromise as PromiseLike<unknown>).then !== 'function'
    ) {
      return;
    }

    setInternalLoading(true);
    try {
      await (maybePromise as PromiseLike<unknown>);
      triggerSuccessState();
    } finally {
      setInternalLoading(false);
    }
  };

  const resolvedType = props.type ?? 'default';
  const variantClassName =
    resolvedType === 'primary'
      ? styles.variantPrimary
      : resolvedType === 'text'
        ? styles.variantText
        : styles.variantDefault;

  const resolvedClassName = [
    styles.button,
    variantClassName,
    props.danger ? styles.variantDanger : null,
    compact ? styles.compact : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const hasStateIndicator = resolvedLoading || showSuccess;

  const renderStateIcon = () => {
    if (resolvedLoading) {
      return (
        <motion.span
          key="loader"
          initial={{ opacity: 0, scale: 0.75 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.75 }}
          className={styles.stateIcon}
        >
          <IconLoader2 className={styles.loaderIcon} />
        </motion.span>
      );
    }

    if (showSuccess) {
      return (
        <motion.span
          key="success"
          initial={{ opacity: 0, scale: 0.75 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.75 }}
          className={styles.stateIcon}
        >
          <IconCheck className={styles.successIcon} />
        </motion.span>
      );
    }

    if (iconOnly) {
      return (
        <motion.span
          key="icon-only-content"
          initial={{ opacity: 0.95, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0.95, scale: 0.98 }}
          className={styles.iconOnlyGlyph}
        >
          {children}
        </motion.span>
      );
    }

    return null;
  };

  return (
    <Button
      title={title}
      {...props}
      className={resolvedClassName}
      style={style}
      loading={false}
      disabled={disabled || resolvedLoading}
      onClick={handleClick}
    >
      <span
        className={[
          styles.content,
          iconOnly ? styles.contentIconOnly : null,
          !iconOnly && hasStateIndicator ? styles.contentWithStateIcon : null,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <AnimatePresence initial={false} mode="wait">
          {renderStateIcon()}
        </AnimatePresence>
        {!iconOnly ? <span className={styles.label}>{children}</span> : null}
      </span>
    </Button>
  );
};
